/**
 * Project Constants
 * Centralized constants for project-related functionality
 */

import { USER_ROLES, VISIBILITY_OPTIONS } from './common'
import { FEATURE_STATUS } from './featureConstants'
import { AUTOMATION_STATUS_VALUES } from './testCaseConstants'
import { BUG_STATUS } from './bugs'

// Re-export common constants for convenience
export const PROJECT_VISIBILITY = VISIBILITY_OPTIONS

// Project status options
export const PROJECT_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  INACTIVE: 'inactive',
  DRAFT: 'draft'
}

export const PROJECT_STATUS_OPTIONS = [
  { value: PROJECT_STATUS.ACTIVE, label: 'Active' },
  { value: PROJECT_STATUS.INACTIVE, label: 'Inactive' },
  { value: PROJECT_STATUS.ARCHIVED, label: 'Archived' },
  { value: PROJECT_STATUS.DRAFT, label: 'Draft' }
]

// Project type options
export const PROJECT_TYPE = {
  WEB: 'web',
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
  API: 'api',
  OTHER: 'other'
}

export const PROJECT_TYPE_OPTIONS = [
  { value: PROJECT_TYPE.WEB, label: 'Web Application' },
  { value: PROJECT_TYPE.MOBILE, label: 'Mobile Application' },
  { value: PROJECT_TYPE.API, label: 'API/Backend' },
  { value: PROJECT_TYPE.DESKTOP, label: 'Desktop Application' },
  { value: PROJECT_TYPE.OTHER, label: 'Other' }
]

// Project visibility options (using imported VISIBILITY_OPTIONS)
export const PROJECT_VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' }
]

// Permission keys for project features
export const PROJECT_PERMISSIONS = {
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',
  PROJECT_ADMIN: 'project:admin',
  PROJECT_READ: 'project:read',
  PROJECT_WRITE: 'project:write'
}

// Project tab configuration
export const PROJECT_TABS = {
  OVERVIEW: {
    id: 'overview',
    name: 'overview',
    route: 'ProjectOverview',
    icon: 'report'
  },
  FEATURES: {
    id: 'features',
    name: 'features',
    route: 'ProjectFeatures',
    icon: 'feature'
  },
  TEST_CASES: {
    id: 'test-cases',
    name: 'test-cases',
    route: 'ProjectTestCases',
    icon: 'testCase'
  },
  TEST_RUNS: {
    id: 'test-runs',
    name: 'test-runs',
    route: 'ProjectTestRuns',
    icon: 'testRun'
  },
  BUGS: {
    id: 'bugs',
    name: 'bugs',
    route: 'ProjectBugs',
    icon: 'bug'
  },
  SETTINGS: {
    id: 'settings',
    name: 'settings',
    route: 'ProjectSettings',
    icon: 'settings',
    requiresPermission: PROJECT_PERMISSIONS.SETTINGS_READ
  }
}

// Helper to get all tabs as array
export const getAllProjectTabs = () => Object.values(PROJECT_TABS)

// Chart color mapping - Tailwind colors to rgba values
// Maps Tailwind color names to their rgba equivalents for Chart.js
export const CHART_COLOR_MAP = {
  blue: 'rgba(59, 130, 246, 0.8)',
  red: 'rgba(239, 68, 68, 0.8)',
  green: 'rgba(34, 197, 94, 0.8)',
  emerald: 'rgba(16, 185, 129, 0.8)',
  yellow: 'rgba(234, 179, 8, 0.8)',
  orange: 'rgba(249, 115, 22, 0.8)',
  purple: 'rgba(168, 85, 247, 0.8)',
  violet: 'rgba(139, 92, 246, 0.8)',
  pink: 'rgba(236, 72, 153, 0.8)',
  fuchsia: 'rgba(217, 70, 239, 0.8)',
  cyan: 'rgba(6, 182, 212, 0.8)',
  teal: 'rgba(20, 184, 166, 0.8)',
  amber: 'rgba(245, 158, 11, 0.8)',
  gray: 'rgba(107, 114, 128, 0.8)',
  rose: 'rgba(244, 63, 94, 0.8)',
  indigo: 'rgba(99, 102, 241, 0.8)',
  sky: 'rgba(14, 165, 233, 0.8)'
}

// Default chart color fallback
export const DEFAULT_CHART_COLOR = 'rgba(59, 130, 246, 0.8)' // blue

// Re-export user roles as project member roles for convenience
export const PROJECT_MEMBER_ROLES = USER_ROLES

// Test run execution status values (for test case execution within test runs)
export const TEST_EXECUTION_STATUS_VALUES = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  SKIPPED: 'skipped',
  PENDING: 'pending'
}

// Project length limits
export const PROJECT_TITLE_MAX_LENGTH = 60
export const PROJECT_DESCRIPTION_MAX_LENGTH = 100

// Tab Icon Colors (Hex values for SVG icons)
export const TAB_ICON_COLORS = {
  OVERVIEW: '#2563eb',      // blue-600 (for project overview)
  FEATURES: '#0891b2',      // cyan-600
  TEST_CASES: '#059669',    // emerald-600
  TEST_RUNS: '#9333ea',     // purple-600
  BUGS: '#dc2626',          // red-600
  ACCESSIBILITY: '#14b8a6', // teal-500
  SETTINGS: '#6b7280',      // gray-600
  // Additional tab colors
  COMMENTS: '#2563eb',      // blue-600
  ACTIVITY: '#059669',      // emerald-600
  PARENT_FEATURE: '#9333ea', // purple-600
  RELATED_BUGS: '#dc2626',  // red-600
  SUBTASKS: '#9333ea',      // purple-600
  REPORTS_OVERVIEW: '#4f46e5', // indigo-600 (for reports overview)
  DEFAULT: '#6b7280'        // gray-600
}

// Helper to get icon color based on tab ID
export const getTabIconColor = (tabId) => {
  const colorMap = {
    [PROJECT_TABS.OVERVIEW.id]: TAB_ICON_COLORS.OVERVIEW,
    [PROJECT_TABS.FEATURES.id]: TAB_ICON_COLORS.FEATURES,
    [PROJECT_TABS.TEST_CASES.id]: TAB_ICON_COLORS.TEST_CASES,
    [PROJECT_TABS.TEST_RUNS.id]: TAB_ICON_COLORS.TEST_RUNS,
    [PROJECT_TABS.BUGS.id]: TAB_ICON_COLORS.BUGS,
    [PROJECT_TABS.SETTINGS.id]: TAB_ICON_COLORS.SETTINGS
  }
  return colorMap[tabId] || TAB_ICON_COLORS.DEFAULT
}

// Helper functions for status comparison
export const isInProgress = (status) => {
  return status === FEATURE_STATUS.IN_PROGRESS || 
         status === 'in_progress' || // Database variant
         status === BUG_STATUS.IN_PROGRESS ||
         status === 'in-progress' // Alternative format
}

export const isInReview = (status) => {
  return status === FEATURE_STATUS.IN_REVIEW || 
         status === 'in_review' // Database variant
}

export const isSemiAutomated = (automationStatus) => {
  return automationStatus === AUTOMATION_STATUS_VALUES.SEMI_AUTOMATED ||
         automationStatus === 'semi_automated' ||
         automationStatus === 'partial' // Legacy format
}

